import _ from 'underscore';
import { Injectable, Injector, Type } from '@angular/core';
import { IObject } from '@type_definitions/datatypes';
import { DialogSize, GaMessageService, IDialog } from '../../message/ga-message.service';
import { ReportGridService } from '../report-grid.service';
import { IReportActionParamDialogOptions, ReportActionsConfigService } from './report-actions-config.service';
import { IReportAction, IReportActionResult, IReportActionResultRow, ReportDataService } from '../report-data.service';
import { GaEventService } from '../../services/ga-event.service';
import { util } from '../../util';
import { BaseReportActionsHandler } from './base-report-actions-handler';
import { GaDeferredService, IGADeferred } from '../../services/ga-deferred.service';
import { BaseReportActionsDialog } from './base-report-actions-dialog';
import { REPORT_ACTION_CANCELLED } from './report-actions.helper';
import { EVENT_REPORT_ACTION_DONE, EVENT_REPORT_ACTION_ERROR } from '../../app-events';
import { ActionStatus } from '../report-style.service';

@Injectable()
export class ReportActionsService {
  private actionDialog: IDialog;

  constructor(
    private messageService: GaMessageService,
    private reportGridService: ReportGridService,
    private reportActionConfigService: ReportActionsConfigService,
    private reportDataService: ReportDataService,
    private eventService: GaEventService,
    private injector: Injector,
    private deferredService: GaDeferredService
  ) {
    /***/
  }

  public executeAction(reportId: number, selectedAction: IReportAction): Promise<boolean> {
    const rowSelection: IObject[] = this.reportGridService.getSelection(reportId.toString());

    // warning: selection of minimum one row
    if (!rowSelection) {
      this.messageService.showErrorDialog('Select rows', 'Please select one or more rows first');
      return Promise.resolve(false);
    }

    const getActionResultBody = (data: IReportActionResult, hasError: boolean): string => {
      let body: string = '';

      // display a message for each individual row
      _.forEach(data.rows, (row: IReportActionResultRow, index) => {
        if (!row.message) {
          return;
        }

        if (row.actionStatus === ActionStatus.ERROR) {
          body += `<div class="action-error action-result-row"><i class="fa fa-times-circle"></i>Error on row ${index}: ${row.message}</div>`;
        } else {
          body += `<div class="action-ok action-result-row"><i class="fa fa-check-circle"></i>${row.message}</div>`;
        }
      });

      if (!body) {
        // display a general message of the action result
        const defaultMessage = hasError? 'Action has errors' : 'Action executed successfully';
        body += data.message || defaultMessage;
      }

      return body;
    };

    const showActionResultDialog = (data:IReportActionResult): void => {
      const hasError:boolean = !!_.find(data.rows, (row:IReportActionResultRow) => row.actionStatus === 'ERROR');

      this.messageService.showDialog({
        title: `${selectedAction.name} Result: ${hasError? 'Error' : 'Success'}`,
        // mess
        message: getActionResultBody(data, hasError),
        dialogClass: `report-action-result-dialog ${hasError? 'error' : 'success'}`
      });

      if (data.rows) {
        this.reportGridService.updateData(reportId.toString(), data.rows);
      }
    };

    this.reportGridService.clearActionStatus(reportId.toString());

    return this.getActionParameters(reportId, selectedAction, rowSelection)
      .then((parameters: IObject) => {
        const reportActionConfig: IReportActionParamDialogOptions = this.reportActionConfigService.getConfig(selectedAction.code);

        // if standalone then just resolve without execute http rest call for report action because it's being handled by handler
        const executeReportAction: Promise<IReportActionResult | boolean> = !!reportActionConfig?.standalone ?
          Promise.resolve(true) : this.reportDataService.executeReportAction(reportId, selectedAction.id, rowSelection, parameters);

        return executeReportAction
          .then((data:IReportActionResult) => {
            this.actionDialog?.hide();
            showActionResultDialog(data);
            return true;
          })
          .catch(() => {
            // send event message that action is error
            this.eventService.sendMessage(EVENT_REPORT_ACTION_ERROR);
            this.actionDialog?.hide();

            return false;
          })
          .finally(() => {
            // send message that action has been executed
            this.eventService.sendMessage(EVENT_REPORT_ACTION_DONE);
          });
      })
      .catch(util.noop);
  }

  private getActionParameters(reportId:number, action:IReportAction, rowSelection: IObject[]): Promise<IObject | void> {
    const paramOptions:IReportActionParamDialogOptions = this.reportActionConfigService.getConfig(action.code);
    let preconditionPromise: Promise<string | void>;

    if (!paramOptions) {
      // when param options is not exist then just resolve it to execute report action rest API call
      return Promise.resolve();
    }

    if (paramOptions.precondition) {
      const preconditionService: BaseReportActionsHandler = this.getHandlers(paramOptions.precondition, reportId, action, rowSelection);
      preconditionPromise = preconditionService.execute();
    } else {
      // resolve it since no precondition defined
      preconditionPromise = Promise.resolve();
    }

    return preconditionPromise
      .then(() => {
        if (paramOptions.component) {
          return this.getParametersFromDialog(reportId, action, rowSelection, paramOptions);
        } else if (paramOptions.handler) {
          return this.getParametersFromHandler(reportId, action, rowSelection, paramOptions);
        } else {
          return Promise.resolve();
        }
      })
      .catch((errorMessage: string) => {
        if (errorMessage !== REPORT_ACTION_CANCELLED) {
          console.error(errorMessage);
          this.messageService.showErrorDialog('Validation failed', errorMessage);
        }
        return Promise.reject();
      });
  }

  private getParametersFromDialog(reportId: number, action: IReportAction, rowSelection: IObject, paramOptions:IReportActionParamDialogOptions): Promise<IObject | string> {
    const deferred: IGADeferred<IObject | string> = this.deferredService.defer();

    this.actionDialog = this.messageService.showDialog({
      component: paramOptions.component,
      size: paramOptions.dialogSize || DialogSize.MD,
      inputs: <BaseReportActionsDialog>{
        title: action.name,
        actionDialogParams: paramOptions.actionDialogParams,
        action,
        reportId,
        rowSelection
      },
      outputs: {
        onSave: onDialogResolve.bind(this),
        onCancel: onDialogCancel.bind(this)
      },
      wrapperComponent: false
    });

    function onDialogResolve(params: IObject): void {
      deferred.resolve(params);
    }

    function onDialogCancel(): void {
      deferred.reject(REPORT_ACTION_CANCELLED);
      this.actionDialog.hide();
    }

    return deferred.promise;
  }

  private getParametersFromHandler(reportId:number, action:IReportAction, rowSelection:IObject[], paramOptions:IReportActionParamDialogOptions): Promise<any> {
    const handlerService: BaseReportActionsHandler = this.getHandlers(paramOptions.handler, reportId, action, rowSelection);
    return handlerService.execute();
  }

  public shouldReloadRows(action:IReportAction):boolean {
    return this.reportActionConfigService.getConfig(action.code)?.reloadRows;
  }

  private getHandlers(handler: Type<BaseReportActionsHandler>, reportId: number, action: IReportAction, rowSelection: IObject[]): BaseReportActionsHandler {
    const service: BaseReportActionsHandler = this.injector.get(handler);
    service.reportId = reportId;
    service.action = action;
    service.rowSelection = rowSelection;

    return service;
  }
}